import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, HomeIcon, UserIcon, RefreshCwIcon, WrenchIcon, HelpCircleIcon } from 'lucide-react';
interface BrandedQuoteFormProps {
  initialZip: string;
  onClose: () => void;
}
export function BrandedQuoteForm({
  initialZip,
  onClose
}: BrandedQuoteFormProps) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    // Hidden fields
    alid: '',
    oid: '',
    source: 'branded_landing_page',
    subID1: '',
    subID2: '',
    subID3: '',
    EBRDate: '',
    ip: '',
    cert: '',
    // New qualifying fields
    projectNature: '',
    windowCount: '',
    homeOwnership: '',
    // Existing visible fields
    taskName: 'Windows',
    address1: '',
    city: '',
    state: '',
    postal_code: initialZip || '',
    first_name: '',
    last_name: '',
    email: '',
    phone_primary: ''
  });
  // Load TrustedForm script and capture cert URL
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
    // Poll for the TrustedForm cert URL
    const interval = setInterval(() => {
      try {
        const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
        if (certInput && certInput.value) {
          setFormData(prev => ({
            ...prev,
            cert: certInput.value
          }));
          clearInterval(interval);
        }
      } catch (e) {
        // Silently continue polling
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  // Fetch IP address
  useEffect(() => {
    try {
      fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => {
        if (data?.ip) {
          setFormData(prev => ({
            ...prev,
            ip: data.ip
          }));
        }
      }).catch(() => {
        // IP detection not available in this environment
      });
    } catch (e) {
      // Fetch not available
    }
  }, []);
  const totalSteps = 5;
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTimeout(() => handleNext(), 300);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Capture final cert value before submit
    const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
    const finalCert = certInput?.value || formData.cert;
    const payload = {
      ...formData,
      cert: finalCert
    };
    console.log('Submitting payload to Champion:', payload);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
    }, 1500);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const ChampionHeader = () => <header className="bg-white border-b border-gray-200 py-4 px-6 flex justify-between items-center sticky top-0 z-10" data-id="element-125">
      <div className="flex flex-col" data-id="element-126">
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-[-4px]" data-id="element-127">
          Partner
        </span>
        <div className="flex items-baseline gap-2" data-id="element-128">
          <span className="text-2xl md:text-3xl font-black text-[#CC0000] tracking-tighter italic" data-id="element-129">
            CHAMPION
          </span>
        </div>
        <span className="text-[10px] md:text-xs font-semibold text-[#003DA5]" data-id="element-130">
          Windows • Sunrooms • Home Exteriors
        </span>
      </div>
      <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-[#CC0000] transition-colors flex items-center gap-1" data-id="element-131">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-132" />
        <span className="hidden sm:inline" data-id="element-133">Exit</span>
      </button>
    </header>;
  if (isSuccess) {
    return <div className="min-h-screen bg-[#F5F5F5] flex flex-col" data-id="element-134">
        <ChampionHeader data-id="element-135" />
        <div className="flex-1 flex items-center justify-center p-6" data-id="element-136">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center border-t-4 border-[#CC0000]" data-id="element-137">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-138">
              <CheckCircle2Icon className="w-12 h-12 text-green-600" data-id="element-139" />
            </div>
            <h2 className="text-2xl font-bold text-[#003DA5] mb-4" data-id="element-140">
              Quote Request Received!
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-141">
              Thank you, {formData.first_name}! A Champion representative will
              contact you shortly at {formData.phone_primary} to discuss your
              window {formData.projectNature.toLowerCase()} project in{' '}
              {formData.city}.
            </p>
            <button onClick={onClose} className="w-full h-14 bg-[#CC0000] hover:bg-red-800 text-white font-bold rounded-xl transition-colors" data-id="element-142">
              Return to Home
            </button>
          </motion.div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#F5F5F5] flex flex-col" data-id="element-143">
      <ChampionHeader data-id="element-144" />

      {/* TrustedForm hidden input */}
      <input type="hidden" name="xxTrustedFormCertUrl" data-id="element-145" />

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 h-2" data-id="element-146">
        <motion.div className="bg-[#CC0000] h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-147" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-148">
        <div className="w-full max-w-2xl" data-id="element-149">
          <div className="mb-6 h-8" data-id="element-150">
            {step > 1 && <button onClick={handleBack} className="text-gray-500 hover:text-[#CC0000] font-medium flex items-center gap-2 transition-colors" data-id="element-151">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-152" /> Back
              </button>}
          </div>

          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-gray-100" data-id="element-153">
            <AnimatePresence mode="wait" custom={direction} data-id="element-154">
              {/* STEP 1: Nature of Project */}
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-155">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#003DA5] mb-8 text-center" data-id="element-156">
                    What is the nature of your windows project?
                  </h1>

                  <div className="flex flex-col gap-4" data-id="element-157">
                    {[{
                  value: 'Replace',
                  desc: 'I need to replace existing windows',
                  icon: <RefreshCwIcon className="w-6 h-6" data-id="element-158" />
                }, {
                  value: 'Repair',
                  desc: 'I need to repair existing windows',
                  icon: <WrenchIcon className="w-6 h-6" data-id="element-159" />
                }, {
                  value: 'Not Sure',
                  desc: "I'm not sure yet — I'd like an expert opinion",
                  icon: <HelpCircleIcon className="w-6 h-6" data-id="element-160" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('projectNature', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.projectNature === option.value ? 'border-[#CC0000] bg-red-50 shadow-md' : 'border-gray-200 bg-white hover:border-red-300 hover:shadow-sm'}`} data-id="element-161">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.projectNature === option.value ? 'bg-red-100 text-[#CC0000]' : 'bg-gray-50 text-[#003DA5]'}`} data-id="element-162">
                          {option.icon}
                        </div>
                        <div data-id="element-163">
                          <div className="font-bold text-gray-900 text-lg" data-id="element-164">
                            {option.value}
                          </div>
                          <div className="text-gray-500 text-sm" data-id="element-165">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 2: How Many Windows */}
              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-166">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#003DA5] mb-8 text-center" data-id="element-167">
                    How many windows are involved?
                  </h1>

                  <div className="grid grid-cols-2 gap-4" data-id="element-168">
                    {['6+', '3-5', '2', '1'].map((count, idx) => <button key={idx} onClick={() => handleOptionSelect('windowCount', count)} className={`p-8 rounded-xl border-2 flex flex-col items-center justify-center gap-3 transition-all duration-200 ${formData.windowCount === count ? 'border-[#CC0000] bg-red-50 shadow-md' : 'border-gray-200 bg-white hover:border-red-300 hover:shadow-sm'}`} data-id="element-169">
                        <span className="font-bold text-gray-900 text-2xl" data-id="element-170">
                          {count}
                        </span>
                        <span className="text-gray-500 text-sm" data-id="element-171">
                          {count === '1' ? 'window' : 'windows'}
                        </span>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 3: Homeownership */}
              {step === 3 && <motion.div key="step3" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-172">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#003DA5] mb-8 text-center" data-id="element-173">
                    Do you own your home?
                  </h1>

                  <div className="flex flex-col gap-4" data-id="element-174">
                    {[{
                  value: 'Yes',
                  desc: 'I own this property'
                }, {
                  value: 'No - But I am Allowed to make changes',
                  desc: 'I rent but have permission from my landlord'
                }, {
                  value: 'No',
                  desc: 'I rent and do not have permission'
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('homeOwnership', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.homeOwnership === option.value ? 'border-[#CC0000] bg-red-50 shadow-md' : 'border-gray-200 bg-white hover:border-red-300 hover:shadow-sm'}`} data-id="element-175">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.homeOwnership === option.value ? 'bg-red-100 text-[#CC0000]' : 'bg-gray-50 text-[#003DA5]'}`} data-id="element-176">
                          <UserIcon className="w-6 h-6" data-id="element-177" />
                        </div>
                        <div data-id="element-178">
                          <div className="font-bold text-gray-900 text-lg" data-id="element-179">
                            {option.value}
                          </div>
                          <div className="text-gray-500 text-sm" data-id="element-180">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 4: Address Information */}
              {step === 4 && <motion.div key="step4" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-181">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#003DA5] mb-8 text-center" data-id="element-182">
                    Where is your project located?
                  </h1>

                  <div className="space-y-5" data-id="element-183">
                    <div data-id="element-184">
                      <label htmlFor="address1" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-185">
                        Street Address
                      </label>
                      <input type="text" id="address1" name="address1" value={formData.address1} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent" placeholder="123 Main St" data-id="element-186" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-187">
                      <div data-id="element-188">
                        <label htmlFor="city" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-189">
                          City
                        </label>
                        <input type="text" id="city" name="city" value={formData.city} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent" placeholder="City" data-id="element-190" />
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-191">
                        <div data-id="element-192">
                          <label htmlFor="state" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-193">
                            State
                          </label>
                          <select id="state" name="state" value={formData.state} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent appearance-none" data-id="element-194">
                            <option value="" data-id="element-195">Select</option>
                            <option value="AL" data-id="element-196">AL</option>
                            <option value="AK" data-id="element-197">AK</option>
                            <option value="AZ" data-id="element-198">AZ</option>
                            <option value="AR" data-id="element-199">AR</option>
                            <option value="CA" data-id="element-200">CA</option>
                            <option value="CO" data-id="element-201">CO</option>
                            <option value="CT" data-id="element-202">CT</option>
                            <option value="DE" data-id="element-203">DE</option>
                            <option value="FL" data-id="element-204">FL</option>
                            <option value="GA" data-id="element-205">GA</option>
                            <option value="HI" data-id="element-206">HI</option>
                            <option value="ID" data-id="element-207">ID</option>
                            <option value="IL" data-id="element-208">IL</option>
                            <option value="IN" data-id="element-209">IN</option>
                            <option value="IA" data-id="element-210">IA</option>
                            <option value="KS" data-id="element-211">KS</option>
                            <option value="KY" data-id="element-212">KY</option>
                            <option value="LA" data-id="element-213">LA</option>
                            <option value="ME" data-id="element-214">ME</option>
                            <option value="MD" data-id="element-215">MD</option>
                            <option value="MA" data-id="element-216">MA</option>
                            <option value="MI" data-id="element-217">MI</option>
                            <option value="MN" data-id="element-218">MN</option>
                            <option value="MS" data-id="element-219">MS</option>
                            <option value="MO" data-id="element-220">MO</option>
                            <option value="MT" data-id="element-221">MT</option>
                            <option value="NE" data-id="element-222">NE</option>
                            <option value="NV" data-id="element-223">NV</option>
                            <option value="NH" data-id="element-224">NH</option>
                            <option value="NJ" data-id="element-225">NJ</option>
                            <option value="NM" data-id="element-226">NM</option>
                            <option value="NY" data-id="element-227">NY</option>
                            <option value="NC" data-id="element-228">NC</option>
                            <option value="ND" data-id="element-229">ND</option>
                            <option value="OH" data-id="element-230">OH</option>
                            <option value="OK" data-id="element-231">OK</option>
                            <option value="OR" data-id="element-232">OR</option>
                            <option value="PA" data-id="element-233">PA</option>
                            <option value="RI" data-id="element-234">RI</option>
                            <option value="SC" data-id="element-235">SC</option>
                            <option value="SD" data-id="element-236">SD</option>
                            <option value="TN" data-id="element-237">TN</option>
                            <option value="TX" data-id="element-238">TX</option>
                            <option value="UT" data-id="element-239">UT</option>
                            <option value="VT" data-id="element-240">VT</option>
                            <option value="VA" data-id="element-241">VA</option>
                            <option value="WA" data-id="element-242">WA</option>
                            <option value="WV" data-id="element-243">WV</option>
                            <option value="WI" data-id="element-244">WI</option>
                            <option value="WY" data-id="element-245">WY</option>
                          </select>
                        </div>
                        <div data-id="element-246">
                          <label htmlFor="postal_code" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-247">
                            Zip Code
                          </label>
                          <input type="text" id="postal_code" name="postal_code" value={formData.postal_code} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent" placeholder="12345" data-id="element-248" />
                        </div>
                      </div>
                    </div>

                    <button onClick={handleNext} disabled={!formData.address1 || !formData.city || !formData.state || !formData.postal_code} className="w-full mt-6 h-14 bg-[#CC0000] hover:bg-red-800 text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-249">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {/* STEP 5: Contact Information */}
              {step === 5 && <motion.div key="step5" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-250">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#003DA5] mb-2 text-center" data-id="element-251">
                    Who should we send the quote to?
                  </h1>
                  <p className="text-gray-500 text-center mb-8" data-id="element-252">
                    Last step! Your information is secure.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-253">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-254">
                      <div data-id="element-255">
                        <label htmlFor="first_name" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-256">
                          First Name
                        </label>
                        <input type="text" id="first_name" name="first_name" required value={formData.first_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent" placeholder="First Name" data-id="element-257" />
                      </div>
                      <div data-id="element-258">
                        <label htmlFor="last_name" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-259">
                          Last Name
                        </label>
                        <input type="text" id="last_name" name="last_name" required value={formData.last_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent" placeholder="Last Name" data-id="element-260" />
                      </div>
                    </div>

                    <div data-id="element-261">
                      <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-262">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent" placeholder="email@example.com" data-id="element-263" />
                    </div>

                    <div data-id="element-264">
                      <label htmlFor="phone_primary" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-265">
                        Primary Phone
                      </label>
                      <input type="tel" id="phone_primary" name="phone_primary" required value={formData.phone_primary} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:border-transparent" placeholder="(555) 123-4567" data-id="element-266" />
                    </div>

                    <div className="pt-4" data-id="element-267">
                      <button type="submit" disabled={isSubmitting} className="w-full h-16 bg-[#CC0000] hover:bg-red-800 text-white font-bold text-xl rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-red-500/30" data-id="element-268">
                        {isSubmitting ? 'Processing...' : 'Get Your Free Quote'}
                      </button>
                      <p className="text-[11px] text-gray-500 text-center mt-4 leading-tight" data-id="element-269">
                        By clicking "Get Your Free Quote", you authorize
                        Champion Windows and its affiliates to contact you at
                        the phone number and email provided using automated
                        technology. Consent is not a condition of purchase.
                      </p>
                    </div>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}